import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Flex,
  ModalBody,
  Spacer,
  useToast,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { EditorState, convertToRaw } from 'draft-js';
import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { useUpdateCacheOnPostCreation } from '@/components/PostingExperience/useUpdateCacheOnPostCreation';
import { QuestionForm } from '@/components/post-form/QuestionForm';
import { SchedulePostMenu } from '@/components/post-form/SchedulePostMenu';
import { analyzeContent, post_created } from '@/analytics';
import { useAiTagging } from '@/hooks/useAiTagging';
import { usePermissions } from '@/hooks/usePermissions';
import { logError } from '@/imports/logging/ClientLogger';
import { AnnouncePostModal } from '@/imports/ui/components/feed-item/AnnouncePostModal';

import {
  CreateQuestionModal_createQuestion,
  CreateQuestionModal_createQuestionVariables,
} from './graphql-types/CreateQuestionModal_createQuestion';

import { PostingModalFooter } from '../PostingModalFooter';

const TAGS_FOR_AI = gql`
  query CreateQuestionModal_TagsForAi($input: MultiTagInput) {
    tagsWithSearchTerm(input: $input) {
      tags {
        _id
        name
      }
    }
  }
`;

const CREATE_QUESTION = gql`
  mutation CreateQuestionModal_createQuestion($input: CreateQuestionInput_V2!) {
    createQuestionV2(input: $input) {
      data {
        _id
        group {
          _id
          isGlobalSingleton
        }
        author {
          _id
          userOnboardingChecklistState {
            userHasPosted
          }
        }
        ...UseUpdateCacheOnPostCreation_PostV2Fragment
      }
    }
  }
  ${useUpdateCacheOnPostCreation.postFragment}
`;

type ICreateQuestionModalProps = {
  onClose: () => void;
  organization?: { _id: string };
  group?: { _id: string };
  defaultIsSponsoredPost?: boolean;
  tags?: { _id: string; name: string }[];
};

type QuestionFormValues = {
  title: string;
  group: string;
  images: ({ imageId: string } | { videoId: string })[];
  body: EditorState;
  isSponsoredPost: boolean;
  isAnnouncement: boolean;
  tags?: { _id: string; name: string }[];
  attachments: { versionFileId: string; fileName: string }[];
  scheduledPublishDate: Date | null;
};

export const CreateQuestionModal: React.FC<ICreateQuestionModalProps> = (
  props
) => {
  const [isAnnounceModalOpen, setIsAnnounceModalOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const permissions = usePermissions();
  const canSchedulePosts = permissions.canDoAdminStuffAndAuthenticated;

  const toast = useToast();

  const methods = useForm<QuestionFormValues>({
    defaultValues: {
      title: '',
      group: props.group?._id,
      images: [],
      body: EditorState.createEmpty(),
      isSponsoredPost: !!props.defaultIsSponsoredPost,
      isAnnouncement: false,
      tags: (props.tags || []).filter((t) => !!t?._id && !!t?.name),
      attachments: [],
      scheduledPublishDate: null,
    },
  });
  const { handleSubmit, formState, watch, setValue, getValues } = methods;

  const isAnnouncement = watch('isAnnouncement');
  const scheduledPublishDate = watch('scheduledPublishDate');
  const title = watch('title');
  const body = watch('body');

  const { data: tagsData } = useQuery(TAGS_FOR_AI, {
    variables: {
      input: { limit: 500, offset: 0, filter: { searchText: '' } },
    },
  });
  const aiLabels = tagsData?.tagsWithSearchTerm?.tags ?? [];

  const { recommendedHashtags } = useAiTagging({
    title,
    bodyPlainText: body?.getCurrentContent?.()?.getPlainText?.() ?? '',
    labels: aiLabels,
    enabled: true,
    onResult: React.useCallback(
      ({ selectedHashtags }) => {
        const current = (getValues('tags') ?? []).filter(
          (t: { _id?: string; name?: string }) => !!t?._id && !!t?.name
        );
        const currentIds = new Set(current.map((t: { _id: string }) => t._id));
        const fromAi = selectedHashtags
          .filter((t) => !!t?._id && !!t?.name)
          .filter((t) => !currentIds.has(t._id));
        if (fromAi.length > 0) {
          setValue('tags', [...current, ...fromAi], {
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      },
      [getValues, setValue]
    ),
  });

  const [createQuestion] = useMutation<
    CreateQuestionModal_createQuestion,
    CreateQuestionModal_createQuestionVariables
  >(CREATE_QUESTION, { refetchQueries: ['AccountMenu_Query'] });

  const { updateCacheOnPostCreation } = useUpdateCacheOnPostCreation();

  const onSubmit = async (params: QuestionFormValues) => {
    setLoading(true);

    try {
      const result = await createQuestion({
        variables: {
          input: {
            data: {
              ...(props.organization
                ? params.isSponsoredPost
                  ? {
                      sponsorData: {
                        isSponsoredPost: true,
                        audience: { groupId: params.group },
                      },
                      organizationId: props.organization._id,
                    }
                  : { organizationId: props.organization._id }
                : { groupId: params.group }),
              versionFileIds: params.attachments.map((attachment) => {
                return attachment.versionFileId;
              }),
              embeddedImageIds: params.images
                .filter((id): id is { imageId: string } => 'imageId' in id)
                .map((id) => id.imageId),
              tagIds: (params.tags ?? [])
                .map((tag) => tag?._id)
                .filter((id): id is string => !!id),
              title: params.title,
              body: convertToRaw(params.body.getCurrentContent()),
              ...(recommendedHashtags.length > 0
                ? { recommendedHashtags }
                : {}),
              scheduledPublishDate: params.scheduledPublishDate?.toISOString(),
            },
            isAnnouncement: params.isAnnouncement,
          },
        },
        update(cache, { data }) {
          if (!data?.createQuestionV2.data) {
            return;
          }
          updateCacheOnPostCreation({
            cache,
            post: data.createQuestionV2.data,
          });
        },
      });

      const bodyText = params.body.getCurrentContent().getPlainText();
      const titleText = params.title || '';
      const fullText = `${titleText}\n${bodyText}`;
      const contentAnalysis = analyzeContent(fullText);

      post_created({
        entity_type: 'post',
        entity_id: result.data?.createQuestionV2?.data?._id,
        post_type: 'question',
        content_type: 'question',
        content_has_tags: !!params.tags && params.tags.length > 0,
        content_has_media:
          params.images.length > 0 || params.attachments.length > 0,
        content_media_count: params.images.length + params.attachments.length,
        post_is_announcement: params.isAnnouncement,
        post_is_scheduled: !!params.scheduledPublishDate,
        location_posted_in: props.organization
          ? 'organization'
          : result.data?.createQuestionV2.data?.group?.isGlobalSingleton
          ? 'home'
          : 'group',
        location_group_id: !props.organization ? params.group : undefined,
        location_organization_id: props.organization?._id,
        content_text: contentAnalysis.contentText,
        content_length: contentAnalysis.contentLength,
        content_word_count: contentAnalysis.wordCount,
        content_has_question_mark: contentAnalysis.hasQuestionMark,
        content_has_links: contentAnalysis.hasLinks,
        content_link_count: contentAnalysis.linkCount,
        content_mention_count: contentAnalysis.mentionCount,
        content_hashtag_count: contentAnalysis.hashtagCount,
        content_hashtags: contentAnalysis.hashtags,
      });
      props.onClose();

      params.scheduledPublishDate &&
        toast({
          description: `Question scheduled for ${dayjs(
            params.scheduledPublishDate
          ).format('MMM D, h:mm A')}`,
          status: 'success',
        });
    } catch (err) {
      toast({
        status: 'error',
        description: 'Failed to create question.',
      });
      logError(err);
    } finally {
      setLoading(false);
    }
  };

  const onInvalid = () => {
    /**
     * If the form is invalid, reset `scheduledPublishDate`
     * User should be able to click `Post` to create the post
     * immediately after the form fields become valid
     */
    methods.resetField('scheduledPublishDate');
  };

  return (
    <FormProvider {...methods}>
      <AnnouncePostModal
        open={isAnnounceModalOpen}
        onClose={() => setIsAnnounceModalOpen(false)}
        onClickAnnounce={() => {
          handleSubmit(onSubmit, onInvalid)();
          setIsAnnounceModalOpen(false);
        }}
        onClickPost={() => {
          methods.setValue('isAnnouncement', false);
          handleSubmit(onSubmit, onInvalid)();
          setIsAnnounceModalOpen(false);
        }}
        announcementLoading={false}
        loading={false}
      />

      <ModalBody px="6" py="4">
        <QuestionForm
          organizationId={props.organization?._id}
          initialGroupId={props.group?._id}
        />
      </ModalBody>

      <PostingModalFooter>
        <Flex w="100%" align="center">
          <Button
            variant="ghost"
            colorScheme="gray"
            onClick={props.onClose}
            display={{ base: 'inline-flex', md: 'inline-flex', lg: 'none' }}
            mr={2}
            border="1px solid"
            borderColor="gray.300"
          >
            Cancel
          </Button>
          <Spacer />
          {canSchedulePosts && (
            <Controller
              name="scheduledPublishDate"
              render={({ field: { onBlur, onChange, ref, value, name } }) => (
                <Box mr={2}>
                  <SchedulePostMenu
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    isDisabled={
                      (formState.isSubmitted && !formState.isValid) ||
                      loading
                    }
                    validate={() => {
                      if (!value) {
                        methods.setError(name, {
                          message: 'A date and time must be specified',
                        });
                        return false;
                      } else {
                        methods.clearErrors(name);
                        return true;
                      }
                    }}
                    validationMessage={
                      formState.errors.scheduledPublishDate?.message
                    }
                  />
                </Box>
              )}
            />
          )}
          <Button
            isLoading={loading}
            isDisabled={formState.isSubmitted && !formState.isValid}
            onClick={
              isAnnouncement
                ? () => setIsAnnounceModalOpen(true)
                : handleSubmit(onSubmit, onInvalid)
            }
          >
            {scheduledPublishDate ? 'Schedule Post' : 'Post Now'}
          </Button>
        </Flex>
      </PostingModalFooter>
    </FormProvider>
  );
};
